'use client';

import React from 'react';

import { Box, BoxProps } from './box';
import { Text } from './text';
import { VStack } from './stack';

// Stat Root Component
export interface StatRootProps extends BoxProps {
  children: React.ReactNode;
}

const StatRoot = React.forwardRef<HTMLDivElement, StatRootProps>(({ children, ...props }, ref) => {
  return (
    <Box ref={ref} {...props}>
      <VStack align="flex-start" gap={1}>
        {children}
      </VStack>
    </Box>
  );
});

StatRoot.displayName = 'StatRoot';

// Stat Label Component
export interface StatLabelProps extends BoxProps {
  children: React.ReactNode;
}

const StatLabel = React.forwardRef<HTMLDivElement, StatLabelProps>(
  ({ children, ...props }, ref) => {
    return (
      <Text
        ref={ref}
        fontSize="sm"
        fontWeight="medium"
        color="#718096"
        textTransform="uppercase"
        letterSpacing="wider"
        {...props}
      >
        {children}
      </Text>
    );
  }
);

StatLabel.displayName = 'StatLabel';

// Stat Number Component
export interface StatNumberProps extends BoxProps {
  children: React.ReactNode;
  fontSize?: string;
  fontWeight?: string | number;
  color?: string;
}

const StatNumber = React.forwardRef<HTMLDivElement, StatNumberProps>(
  ({ children, fontSize = '2xl', fontWeight = 'bold', color = '#1A202C', ...props }, ref) => {
    return (
      <Text
        ref={ref}
        fontSize={fontSize}
        fontWeight={fontWeight}
        color={color}
        lineHeight="1.2"
        {...props}
      >
        {children}
      </Text>
    );
  }
);

StatNumber.displayName = 'StatNumber';

// Stat Help Text Component
export interface StatHelpTextProps extends BoxProps {
  children: React.ReactNode;
}

const StatHelpText = React.forwardRef<HTMLDivElement, StatHelpTextProps>(
  ({ children, ...props }, ref) => {
    return (
      <Box ref={ref} fontSize="sm" color="#718096" {...props}>
        {children}
      </Box>
    );
  }
);

StatHelpText.displayName = 'StatHelpText';

// Stat Arrow Component
export interface StatArrowProps extends BoxProps {
  type?: 'increase' | 'decrease';
}

const StatArrow = React.forwardRef<HTMLDivElement, StatArrowProps>(
  ({ type = 'increase', ...props }, ref) => {
    const isIncrease = type === 'increase';

    return (
      <Box
        ref={ref}
        display="inline-flex"
        alignItems="center"
        fontSize="sm"
        color={isIncrease ? '#38A169' : '#E53E3E'}
        {...props}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: isIncrease ? 'none' : 'rotate(180deg)',
            marginRight: '4px',
          }}
        >
          <path display="M6 3L9 6L3 6L6 3Z" fill="currentColor" />
        </svg>
      </Box>
    );
  }
);

StatArrow.displayName = 'StatArrow';

// Compound component exports
export const Stat = {
  Root: StatRoot,
  Label: StatLabel,
  Number: StatNumber,
  HelpText: StatHelpText,
  Arrow: StatArrow,
};

// Individual exports for direct usage
export { StatRoot, StatLabel, StatNumber, StatHelpText, StatArrow };
